/**
 * 手机陀螺仪 / 设备方向控制器。
 *
 * 输出与 InputController 同形状：get() → { active, x, y }，x/y ∈ [-1, 1]。
 *
 * 设计要点：
 * - iOS 13+ Safari 需要在用户手势中调用 DeviceOrientationEvent.requestPermission()。
 *   enable() 必须由按钮点击等事件间接触发，且页面需 https/localhost。
 * - 校准：启用时记录当前 (beta, gamma) 作为零点，后续以"相对当前姿态的偏移"映射输入，
 *   支持躺着玩/站着玩。用户可再次点按 calibrate() 重新归零。
 * - 屏幕旋转：根据 screen.orientation.angle（或 window.orientation）把设备轴互换到
 *   屏幕轴，保证竖握/横握下 "手机右倾 → 球向右"。
 * - 灵敏度：ORIENTATION_FULL_TILT_DEG（默认 18°）物理倾斜 = 输入满值 1。
 */

const STORAGE_KEY_ENABLED = 'gravity-maze-orientation-enabled';

/** 物理倾斜角（度）映射到 input=1 的临界值。越小越灵敏。 */
export const ORIENTATION_FULL_TILT_DEG = 18;

/** 小于此 delta 角（度）时视为静止，避免噪声带来的持续抖动。 */
const DEAD_ZONE_DEG = 0.5;

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/** 取屏幕相对设备的旋转角（度）。兼容 screen.orientation 与老 API window.orientation。 */
function getScreenAngle() {
  if (typeof window === 'undefined') return 0;
  const so = window.screen && window.screen.orientation;
  if (so && typeof so.angle === 'number') return so.angle;
  if (typeof window.orientation === 'number') return window.orientation;
  return 0;
}

/**
 * 把设备 (beta, gamma) 的 delta 映射为屏幕 (sx, sy) 的 delta，单位仍为度。
 *  - beta  ：设备绕 X 轴（前后倾）；竖握时朝玩家身侧=正
 *  - gamma ：设备绕 Y 轴（左右倾）；竖握时右倾=正
 *
 * 游戏坐标：sx 右正、sy 下正（与触摸坐标一致）。
 */
function deviceToScreen(dBeta, dGamma, screenAngle) {
  // 竖持(angle=0)：手机右倾(gamma+)→屏幕右；手机朝玩家倾(beta+)→屏幕下
  // 其余旋转角按二维旋转公式互换
  switch (screenAngle) {
    case 90:
      return { sx: -dBeta, sy: dGamma };
    case -90:
    case 270:
      return { sx: dBeta, sy: -dGamma };
    case 180:
      return { sx: -dGamma, sy: -dBeta };
    case 0:
    default:
      return { sx: dGamma, sy: dBeta };
  }
}

export class OrientationController {
  constructor() {
    this._enabled = false;
    this._zero = null; // { beta, gamma }
    this._last = null; // 最近一次事件（用于校准）
    this._state = { active: false, x: 0, y: 0 };
    this._onOrientation = this._onOrientation.bind(this);
  }

  /** 环境是否提供 DeviceOrientationEvent API。 */
  static isSupported() {
    return typeof window !== 'undefined' && typeof window.DeviceOrientationEvent !== 'undefined';
  }

  /** iOS 13+ 需要显式授权。其他平台返回 false。 */
  static requiresPermission() {
    return (
      OrientationController.isSupported() &&
      typeof DeviceOrientationEvent.requestPermission === 'function'
    );
  }

  /** 用户是否在之前会话里开启过（UI 可据此恢复按钮高亮状态）。 */
  static wasEnabled() {
    try {
      return localStorage.getItem(STORAGE_KEY_ENABLED) === '1';
    } catch {
      return false;
    }
  }

  isEnabled() {
    return this._enabled;
  }

  /**
   * 请求权限并开始监听。必须由用户手势触发（按钮 click 事件内）。
   * @returns {Promise<{ ok: boolean, reason?: string }>}
   */
  async enable() {
    if (this._enabled) return { ok: true };
    if (!OrientationController.isSupported()) {
      return { ok: false, reason: '当前浏览器不支持陀螺仪' };
    }
    if (OrientationController.requiresPermission()) {
      let res;
      try {
        res = await DeviceOrientationEvent.requestPermission();
      } catch (e) {
        return { ok: false, reason: `获取权限失败：${e?.message || e}` };
      }
      if (res !== 'granted') {
        return { ok: false, reason: '陀螺仪权限未授予（iOS 需在 HTTPS 下使用）' };
      }
    }
    window.addEventListener('deviceorientation', this._onOrientation);
    this._enabled = true;
    this._zero = null; // 首个事件到来时自动校准
    try {
      localStorage.setItem(STORAGE_KEY_ENABLED, '1');
    } catch {
      /* ignore */
    }
    return { ok: true };
  }

  /** 停止监听并清空状态。 */
  disable() {
    if (!this._enabled) return;
    window.removeEventListener('deviceorientation', this._onOrientation);
    this._enabled = false;
    this._zero = null;
    this._last = null;
    this._state = { active: false, x: 0, y: 0 };
    try {
      localStorage.setItem(STORAGE_KEY_ENABLED, '0');
    } catch {
      /* ignore */
    }
  }

  /** 以当前姿态作为新零点。 */
  calibrate() {
    if (this._last) {
      this._zero = { beta: this._last.beta, gamma: this._last.gamma };
      this._state = { active: true, x: 0, y: 0 };
    } else {
      // 尚未收到事件，下一次事件到来时自动归零
      this._zero = null;
    }
  }

  get() {
    return this._state;
  }

  dispose() {
    this.disable();
  }

  /**
   * 直接喂入事件数据（便于测试或自定义源）。
   * @param {{ beta: number|null, gamma: number|null }} ev
   */
  feedEvent(ev) {
    this._onOrientation(ev);
  }

  _onOrientation(ev) {
    const beta = typeof ev.beta === 'number' ? ev.beta : null;
    const gamma = typeof ev.gamma === 'number' ? ev.gamma : null;
    if (beta === null || gamma === null) return;
    this._last = { beta, gamma };
    if (!this._zero) {
      this._zero = { beta, gamma };
      this._state = { active: true, x: 0, y: 0 };
      return;
    }
    const dBeta = beta - this._zero.beta;
    const dGamma = gamma - this._zero.gamma;
    const { sx, sy } = deviceToScreen(dBeta, dGamma, getScreenAngle());
    // 死区抑制噪声
    const full = ORIENTATION_FULL_TILT_DEG;
    const ax = Math.abs(sx) < DEAD_ZONE_DEG ? 0 : sx / full;
    const ay = Math.abs(sy) < DEAD_ZONE_DEG ? 0 : sy / full;
    this._state = {
      active: true,
      x: clamp(ax, -1, 1),
      y: clamp(ay, -1, 1),
    };
  }
}

// 导出内部函数以便测试
export const __test__ = { deviceToScreen };
